"use client";

import { c as _c } from "react/compiler-runtime";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import CommonSkeletonLoader from "@/components/utils/animation/CommonSkeletonLoader";
import { RatingWithProfileInfoAndLevel } from "@/entities/IProfile";
import { SortDirection } from "@/entities/ISort";
import { ProfileRatersParamsOrderBy, ProfileRatersTableType, RateMatter } from "@/enums";
import { assertUnreachable } from "@/helpers/AllowlistToolHelpers";
import { Page } from "@/helpers/Types";
import { commonApiFetch } from "@/services/api/common-api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";
import ProfileRatersTable from "../ProfileRatersTable";
import ProfileRatersTableWrapperHeader from "./ProfileRatersTableWrapperHeader";
export interface ProfileRatersParams {
  readonly page: number;
  readonly pageSize: number;
  readonly given: boolean;
  readonly order: SortDirection;
  readonly orderBy: ProfileRatersParamsOrderBy;
  readonly handleOrWallet: string;
  readonly matter: RateMatter;
}
export default function ProfileRatersTableWrapper(t0) {
  const $ = _c(37);
  const {
    initialParams
  } = t0;
  const params = useParams();
  let t1;
  if ($[0] !== params?.user) {
    t1 = (params?.user as string)?.toLowerCase();
    $[0] = params?.user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleOrWallet = t1;
  const pageSize = initialParams.pageSize;
  const given = initialParams.given;
  const matter = initialParams.matter;
  const [currentPage, setCurrentPage] = useState(initialParams.page);
  const [order, setOrder] = useState(initialParams.order);
  const [orderBy, setOrderBy] = useState(initialParams.orderBy);
  let t2;
  if ($[2] !== given || $[3] !== matter) {
    t2 = () => {
      switch (matter) {
        case RateMatter.NIC:
          {
            return given ? ProfileRatersTableType.CIC_GIVEN : ProfileRatersTableType.CIC_RECEIVED;
          }
        case RateMatter.REP:
        case RateMatter.DROP_REP:
          {
            return given ? ProfileRatersTableType.REP_GIVEN : ProfileRatersTableType.REP_RECEIVED;
          }
        default:
          {
            assertUnreachable(matter);
            return ProfileRatersTableType.CIC_RECEIVED;
          }
      }
    };
    $[2] = given;
    $[3] = matter;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const getType = t2;
  const type = getType();
  const t3 = `${currentPage}`;
  const t4 = `${pageSize}`;
  let t5;
  if ($[5] !== given || $[6] !== handleOrWallet || $[7] !== matter || $[8] !== order || $[9] !== orderBy || $[10] !== t3 || $[11] !== t4) {
    t5 = [QueryKey.PROFILE_RATERS, {
      handleOrWallet,
      matter,
      page: t3,
      pageSize: t4,
      order,
      orderBy,
      given
    }];
    $[5] = given;
    $[6] = handleOrWallet;
    $[7] = matter;
    $[8] = order;
    $[9] = orderBy;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== currentPage || $[14] !== given || $[15] !== handleOrWallet || $[16] !== matter || $[17] !== order || $[18] !== orderBy || $[19] !== pageSize) {
    t6 = async () => await commonApiFetch({
      endpoint: `profiles/${handleOrWallet}/${matter === RateMatter.NIC ? "cic" : matter.toLowerCase()}/ratings/by-rater`,
      params: {
        page: `${currentPage}`,
        page_size: `${pageSize}`,
        order: order.toLowerCase(),
        order_by: orderBy.toLowerCase(),
        given: given ? "true" : "false"
      }
    });
    $[13] = currentPage;
    $[14] = given;
    $[15] = handleOrWallet;
    $[16] = matter;
    $[17] = order;
    $[18] = orderBy;
    $[19] = pageSize;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const t7 = !!handleOrWallet;
  let t8;
  if ($[21] !== t5 || $[22] !== t6 || $[23] !== t7) {
    t8 = {
      queryKey: t5,
      queryFn: t6,
      enabled: t7,
      placeholderData: keepPreviousData
    };
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  const {
    isLoading,
    isFetching,
    data: ratings
  } = useQuery(t8);
  const [totalPages, setTotalPages] = useState(1);
  let t9;
  if ($[25] !== isLoading || $[26] !== pageSize || $[27] !== ratings) {
    t9 = () => {
      if (isLoading) {
        return;
      }
      if (!ratings?.count) {
        setCurrentPage(1);
        setTotalPages(1);
        return;
      }
      setTotalPages(Math.ceil(ratings.count / pageSize));
    };
    $[25] = isLoading;
    $[26] = pageSize;
    $[27] = ratings;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const t10 = ratings?.count;
  const t11 = ratings?.page;
  let t12;
  if ($[29] !== isLoading || $[30] !== t10 || $[31] !== t11) {
    t12 = [t10, t11, isLoading];
    $[29] = isLoading;
    $[30] = t10;
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  useEffect(t9, t12);
  const getNoRatingsMessage = () => {
    switch (type) {
      case ProfileRatersTableType.CIC_RECEIVED:
        {
          return "No NIC received ratings";
        }
      case ProfileRatersTableType.CIC_GIVEN:
        {
          return "No NIC given ratings";
        }
      case ProfileRatersTableType.REP_RECEIVED:
        {
          return "No Rep received ratings";
        }
      case ProfileRatersTableType.REP_GIVEN:
        {
          return "No Rep given ratings";
        }
      default:
        {
          assertUnreachable(type);
          return "";
        }
    }
  };
  let t13;
  if ($[33] !== orderBy) {
    t13 = newSortType => {
      if (newSortType === orderBy) {
        setOrder(_temp);
      } else {
        setOrderBy(newSortType);
        setOrder(SortDirection.DESC);
      }
    };
    $[33] = orderBy;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const onSortTypeClick = t13;
  const t14 = "tw-flex-1 tw-mt-2 lg:tw-mt-4 tw-bg-iron-950 tw-border tw-border-iron-800 tw-border-solid tw-rounded-xl tw-max-h-[29rem] tw-scroll-py-3 tw-overflow-y-auto tw-overflow-x-auto tw-scrollbar-thin tw-scrollbar-thumb-iron-500 tw-scrollbar-track-iron-800 desktop-hover:hover:tw-scrollbar-thumb-iron-300";
  const t15 = isLoading ? <div className="tw-p-4"><CommonSkeletonLoader /></div> : <ProfileRatersTable ratings={ratings?.data || []} type={type} order={order} orderBy={orderBy} loading={isFetching} totalPages={totalPages} currentPage={currentPage} setCurrentPage={setCurrentPage} onSortTypeClick={onSortTypeClick} noRatingsMessage={getNoRatingsMessage()} />;
  let t16;
  if ($[35] !== t15) {
    t16 = <div className={t14}>{t15}</div>;
    $[35] = t15;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  return <div className="tw-flex-1 tw-min-h-full tw-flex tw-flex-col"><div><ProfileRatersTableWrapperHeader type={type} /></div>{t16}</div>;
}
function _temp(prev) {
  return prev === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC;
}